import { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { toast } from "sonner";
import { ArrowLeft, Sparkles, BookOpen, Brain, Target, MessageCircle, CheckCircle2, Download, RefreshCw, Loader2 } from "lucide-react";
import { ChatPanel } from "@/components/ChatPanel";
import { TestimonialPrompt } from "@/components/TestimonialPrompt";
import { ShareScore } from "@/components/ShareScore";

export default function Analysis() {
  const { moduleId } = useParams();
  const [analysis, setAnalysis] = useState(null);
  const [moduleInfo, setModuleInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const contentRef = useRef(null);

  const load = () =>
    Promise.all([
      api.get(`/modules/${moduleId}/analysis`),
      api.get(`/modules/${moduleId}`),
    ]).then(([a, m]) => {
      setAnalysis(a.data);
      setModuleInfo(m.data.module);
    });

  useEffect(() => {
    load().finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduleId]);

  const downloadPdf = async () => {
    if (!contentRef.current) return;
    setDownloading(true);
    try {
      const canvas = await html2canvas(contentRef.current, {
        scale: 2,
        backgroundColor: "#FAF9F6",
        useCORS: true,
      });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const imgW = pageW;
      const imgH = (canvas.height * imgW) / canvas.width;
      let heightLeft = imgH;
      let position = 0;
      pdf.addImage(imgData, "PNG", 0, position, imgW, imgH);
      heightLeft -= pageH;
      while (heightLeft > 0) {
        position -= pageH;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, imgW, imgH);
        heightLeft -= pageH;
      }
      pdf.save(`analyse-${moduleInfo?.title || moduleId}.pdf`);
    } catch (e) {
      toast.error("Impossible de générer le PDF.");
    } finally {
      setDownloading(false);
    }
  };

  const regenerate = async () => {
    if (!window.confirm("Régénérer cette analyse ? L'analyse actuelle sera remplacée.")) return;
    setRegenerating(true);
    try {
      await api.post(`/modules/${moduleId}/analysis?regenerate=true`);
      await load();
      toast.success("Analyse régénérée.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de régénération");
    } finally {
      setRegenerating(false);
    }
  };

  if (loading)
    return <div className="max-w-3xl mx-auto px-6 py-20 text-center text-[#8A8480]">Chargement de l'analyse…</div>;
  if (!analysis)
    return <div className="max-w-3xl mx-auto px-6 py-20 text-center">Analyse introuvable</div>;

  const score = analysis.compatibility_score || 0;
  const isSolo = analysis.analysis_type === "solo";
  const scoreColor =
    score >= 80 ? "#7A8B76" : score >= 60 ? "#D4A373" : "#C86B5E";

  return (
    <div className="relative">
      <div
        className="absolute inset-0 opacity-30 pointer-events-none"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1628965882741-570e75becd5d?crop=entropy&cs=srgb&fm=jpg&q=60')",
          backgroundSize: "cover",
          backgroundPosition: "top",
          maskImage: "linear-gradient(to bottom, rgba(0,0,0,0.5), transparent 40%)",
          WebkitMaskImage: "linear-gradient(to bottom, rgba(0,0,0,0.5), transparent 40%)",
        }}
      />
      <div ref={contentRef} className="relative max-w-4xl mx-auto px-6 py-12 md:py-16">
        <div className="flex items-center justify-between mb-10" data-html2canvas-ignore>
          <Link
            to="/dashboard"
            data-testid="analysis-back-link"
            className="inline-flex items-center gap-2 text-sm text-[#8A8480] hover:text-[#C86B5E]"
          >
            <ArrowLeft className="w-4 h-4" /> Retour au parcours
          </Link>
          <div className="flex items-center gap-3">
            <button
              data-testid="regenerate-analysis-button"
              onClick={regenerate}
              disabled={regenerating}
              className="inline-flex items-center gap-2 text-sm text-[#5C5754] hover:text-[#C86B5E] border border-[#E6E2DD] rounded-full px-4 py-2 transition-colors disabled:opacity-50"
            >
              {regenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              {regenerating ? "Régénération…" : "Régénérer"}
            </button>
            <button
              data-testid="download-pdf-button"
              onClick={downloadPdf}
              disabled={downloading}
              className="inline-flex items-center gap-2 text-sm text-white bg-[#C86B5E] hover:bg-[#B35A4E] rounded-full px-4 py-2 transition-colors disabled:opacity-50"
            >
              {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              {downloading ? "Préparation…" : "Télécharger en PDF"}
            </button>
          </div>
        </div>

        <p className="text-xs tracking-[0.3em] uppercase text-[#C86B5E] font-semibold mb-3">
          Analyse · {moduleInfo?.title}
        </p>
        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-tight mb-12">
          {isSolo ? (
            <>Voici ce que révèle votre <span className="italic">bilan</span>.</>
          ) : (
            <>Voici ce que vos <span className="italic">cœurs</span> révèlent.</>
          )}
        </h1>

        {/* Score */}
        <div className="bg-white border border-[#E6E2DD] rounded-3xl p-10 mb-8 text-center">
          <p className="text-xs tracking-[0.3em] uppercase text-[#8A8480] font-semibold mb-6">
            {isSolo ? "Score de clarté" : "Score d'alignement"}
          </p>
          <div className="relative inline-flex items-center justify-center" data-testid="compatibility-score">
            <svg width="180" height="180" className="-rotate-90">
              <circle cx="90" cy="90" r="80" stroke="#F3EFEB" strokeWidth="10" fill="none" />
              <circle
                cx="90"
                cy="90"
                r="80"
                stroke={scoreColor}
                strokeWidth="10"
                fill="none"
                strokeDasharray={`${(score / 100) * 502.4} 502.4`}
                strokeLinecap="round"
                style={{ transition: "stroke-dasharray 1s ease-out" }}
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="font-heading text-5xl" style={{ color: scoreColor }}>
                {score}
              </span>
              <span className="text-xs tracking-[0.2em] uppercase text-[#8A8480] mt-1">sur 100</span>
            </div>
          </div>
          <div className="mt-8">
            <ShareScore
              score={score}
              label={isSolo ? "Score de clarté" : "Score d'alignement"}
              moduleTitle={moduleInfo?.title || "Notre parcours"}
              scoreColor={scoreColor}
            />
          </div>
        </div>

        {/* Synthesis */}
        <div className="bg-white border border-[#E6E2DD] rounded-3xl p-8 md:p-10 mb-8" data-testid="analysis-synthesis">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-[#F3EFEB] flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-[#C86B5E]" strokeWidth={1.5} />
            </div>
            <h2 className="font-heading text-2xl">Synthèse</h2>
          </div>
          <p className="text-lg text-[#2C2A29] leading-relaxed font-light">
            {analysis.synthesis}
          </p>
        </div>

        {/* Strengths & Divergences */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <div className="bg-white border border-[#E6E2DD] rounded-3xl p-8" data-testid="analysis-strengths">
            <div className="flex items-center gap-2 mb-5">
              <CheckCircle2 className="w-5 h-5 text-[#7A8B76]" strokeWidth={1.5} />
              <h3 className="font-heading text-xl">{isSolo ? "Vos forces" : "Points d'alignement"}</h3>
            </div>
            <ul className="space-y-3">
              {(analysis.alignment_strengths || []).map((s, i) => (
                <li key={i} className="flex gap-3 text-[#5C5754]">
                  <span className="text-[#7A8B76] mt-1">•</span>
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-white border border-[#E6E2DD] rounded-3xl p-8" data-testid="analysis-divergences">
            <div className="flex items-center gap-2 mb-5">
              <Target className="w-5 h-5 text-[#C86B5E]" strokeWidth={1.5} />
              <h3 className="font-heading text-xl">{isSolo ? "Points de vigilance" : "Points à explorer"}</h3>
            </div>
            <ul className="space-y-3">
              {(analysis.divergences || []).map((d, i) => (
                <li key={i} className="flex gap-3 text-[#5C5754]">
                  <span className="text-[#C86B5E] mt-1">•</span>
                  <span>{d}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Psychology */}
        <div className="bg-white border border-[#E6E2DD] rounded-3xl p-8 md:p-10 mb-8" data-testid="analysis-psychology">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-[#F3EFEB] flex items-center justify-center">
              <Brain className="w-4 h-4 text-[#C86B5E]" strokeWidth={1.5} />
            </div>
            <h2 className="font-heading text-2xl">Lecture psychologique</h2>
          </div>
          <p className="text-[#2C2A29] leading-relaxed whitespace-pre-wrap">
            {analysis.psychology_insights}
          </p>
        </div>

        {/* Biblical */}
        <div className="bg-[#F3EFEB] border border-[#E6DCD3] rounded-3xl p-8 md:p-10 mb-8" data-testid="analysis-biblical">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center">
              <BookOpen className="w-4 h-4 text-[#C86B5E]" strokeWidth={1.5} />
            </div>
            <h2 className="font-heading text-2xl">Conseil pastoral et théologique</h2>
          </div>
          <p className="text-[#2C2A29] leading-relaxed whitespace-pre-wrap font-light">
            {analysis.biblical_counsel}
          </p>
        </div>

        {/* Action plan */}
        <div className="bg-white border border-[#E6E2DD] rounded-3xl p-8 md:p-10 mb-8" data-testid="analysis-action-plan">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-[#F3EFEB] flex items-center justify-center">
              <Target className="w-4 h-4 text-[#C86B5E]" strokeWidth={1.5} />
            </div>
            <h2 className="font-heading text-2xl">Plan d'action</h2>
          </div>
          <div className="space-y-5">
            {(analysis.action_plan || []).map((a, i) => (
              <div key={i} className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#C86B5E] text-white flex items-center justify-center text-sm font-medium">
                  {i + 1}
                </div>
                <div>
                  <p className="font-medium text-[#2C2A29]">{a.title}</p>
                  <p className="text-[#5C5754] leading-relaxed mt-1">{a.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Discussion */}
        {analysis.discussion_questions?.length > 0 && (
          <div className="bg-white border border-[#E6E2DD] rounded-3xl p-8 md:p-10 mb-8" data-testid="analysis-discussion">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-[#F3EFEB] flex items-center justify-center">
                <MessageCircle className="w-4 h-4 text-[#C86B5E]" strokeWidth={1.5} />
              </div>
              <h2 className="font-heading text-2xl">{isSolo ? "À méditer personnellement" : "À discuter ensemble"}</h2>
            </div>
            <ul className="space-y-4">
              {analysis.discussion_questions.map((q, i) => (
                <li key={i} className="font-heading text-xl italic text-[#2C2A29] leading-snug">
                  « {q} »
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="pt-8" data-html2canvas-ignore>
          <div
            data-testid="analysis-refine-note"
            className="mb-8 bg-[#FAF9F6] border border-[#E6E2DD] rounded-2xl p-6 md:p-7"
          >
            <p className="text-sm text-[#5C5754] leading-relaxed">
              <span className="font-medium text-[#2C2A29]">
                Ce bilan ne vous semble pas tout à fait juste&nbsp;?
              </span>{" "}
              Il se fonde entièrement sur vos réponses. Revenir sur certaines d'entre elles —
              pour les préciser ou les compléter — puis régénérer le bilan permet souvent de
              l'affiner.
            </p>
            <div className="mt-4 flex flex-wrap gap-3">
              <Link
                to={`/module/${moduleId}?edit=1`}
                data-testid="refine-edit-answers-link"
                className="inline-flex items-center gap-2 text-sm border border-[#E6E2DD] hover:border-[#C86B5E] text-[#5C5754] hover:text-[#C86B5E] rounded-full px-5 py-2.5 transition-colors"
              >
                Ajuster mes réponses
              </Link>
              <button
                data-testid="refine-regenerate-button"
                onClick={regenerate}
                disabled={regenerating}
                className="inline-flex items-center gap-2 text-sm text-white bg-[#C86B5E] hover:bg-[#B35A4E] rounded-full px-5 py-2.5 transition-colors disabled:opacity-50"
              >
                {regenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                {regenerating ? "Régénération…" : "Régénérer le bilan"}
              </button>
            </div>
          </div>
          {moduleId === "communication" && <TestimonialPrompt />}
          {analysis.couple_id && (
            <div className="mb-8">
              <p className="text-xs tracking-[0.3em] uppercase text-[#7A8B76] font-semibold mb-4">
                Échanger avec votre pasteur
              </p>
              <ChatPanel
                coupleId={analysis.couple_id}
                scope={moduleId}
                scopeLabel={`Chat · ${moduleInfo?.title || "Ce module"}`}
                compact
              />
            </div>
          )}
          <div className="text-center">
            <Link
              to="/dashboard"
              data-testid="analysis-continue"
              className="bg-[#C86B5E] hover:bg-[#B35A4E] text-white rounded-full px-10 py-4 transition-colors inline-flex items-center gap-3"
            >
              Continuer le parcours
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
